import { useParams } from 'react-router-dom'
import { useMenuStore } from '../../core/stores/menuStore'
import { useTranslation } from '../../core/hooks/useTranslation'
import { useBusinessColor } from '../../core/hooks/useBusinessColor'
import { Card, CardContent } from '../../components/ui/Card'
import { Heart, Award, Users, Clock, MapPin, Phone, Mail } from 'lucide-react'

export default function MenuAboutPage() {
  const { slug } = useParams()
  const { isRTL, t } = useTranslation()
  const { color, darker, opacity15 } = useBusinessColor()
  const { menu } = useMenuStore()

  return (
    <>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div 
            className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg"
            style={{
              background: `linear-gradient(to bottom right, ${color}, ${darker})`,
              boxShadow: `0 10px 15px -3px ${opacity15}, 0 4px 6px -2px ${opacity15}`,
            }}
          >
            <Heart className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-4">
            {isRTL ? 'من نحن' : 'About Us'}
          </h1>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto">
            {isRTL 
              ? 'نحن ملتزمون بتقديم أفضل تجربة طعام لعملائنا مع التركيز على الجودة والخدمة المتميزة'
              : 'We are committed to providing the best dining experience for our customers with a focus on quality and excellent service'
            }
          </p>
        </div>

        {/* About Content */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <Card variant="elevated" className="border-stone-200">
            <CardContent className="p-6">
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                style={{ backgroundColor: opacity15 }}
              >
                <Award className="w-6 h-6" style={{ color }} />
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-2">
                {isRTL ? 'رؤيتنا' : 'Our Vision'}
              </h3>
              <p className="text-stone-600">
                {isRTL 
                  ? 'أن نكون الخيار الأول لعملائنا في تقديم أفضل الأطباق والخدمات مع الحفاظ على أعلى معايير الجودة'
                  : 'To be our customers\' first choice in providing the best dishes and services while maintaining the highest quality standards'
                }
              </p>
            </CardContent>
          </Card>

          <Card variant="elevated" className="border-stone-200">
            <CardContent className="p-6">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-2">
                {isRTL ? 'مهمتنا' : 'Our Mission'}
              </h3>
              <p className="text-stone-600">
                {isRTL 
                  ? 'تقديم تجربة طعام استثنائية تجمع بين النكهات الأصيلة والخدمة المتميزة في بيئة مريحة وودية'
                  : 'To provide an exceptional dining experience that combines authentic flavors and excellent service in a comfortable and friendly environment'
                }
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Values */}
        <Card variant="elevated" className="border-stone-200 mb-12">
          <CardContent className="p-6 sm:p-8">
            <h2 className="text-2xl font-bold text-stone-900 mb-6 text-center">
              {isRTL ? 'قيمنا' : 'Our Values'}
            </h2>
            <div className="grid sm:grid-cols-3 gap-6">
              <div className="text-center">
                <div 
                  className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: opacity15 }}
                >
                  <Heart className="w-8 h-8" style={{ color }} />
                </div>
                <h3 className="font-bold text-stone-900 mb-2">{isRTL ? 'الجودة' : 'Quality'}</h3>
                <p className="text-sm text-stone-600">
                  {isRTL ? 'نستخدم أفضل المكونات' : 'We use the finest ingredients'}
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="font-bold text-stone-900 mb-2">{isRTL ? 'السرعة' : 'Speed'}</h3>
                <p className="text-sm text-stone-600">
                  {isRTL ? 'خدمة سريعة وفعالة' : 'Fast and efficient service'}
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="font-bold text-stone-900 mb-2">{isRTL ? 'الرضا' : 'Satisfaction'}</h3>
                <p className="text-sm text-stone-600">
                  {isRTL ? 'رضا العملاء أولويتنا' : 'Customer satisfaction is our priority'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact Info */}
        {menu?.business && (
          <div className="space-y-6">
            <Card variant="elevated" className="border-stone-200">
              <CardContent className="p-6 sm:p-8">
                <h2 className="text-2xl font-bold text-stone-900 mb-6 text-center">
                  {isRTL ? 'معلومات التواصل' : 'Contact Information'}
                </h2>
                <div className="space-y-6">
                  {menu.business.address && (
                    <div className="flex items-start gap-4">
                      <div 
                        className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: opacity15 }}
                      >
                        <MapPin className="w-6 h-6" style={{ color }} />
                      </div>
                      <div>
                        <h3 className="font-bold text-stone-900 mb-1">{isRTL ? 'العنوان' : 'Address'}</h3>
                        <p className="text-stone-600">{menu.business.address}</p>
                      </div>
                    </div>
                  )}
                  
                  {menu.business.contactNumber && (
                    <div className="flex items-start gap-4">
                      <div 
                        className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: opacity15 }}
                      >
                        <Phone className="w-6 h-6" style={{ color }} />
                      </div>
                      <div>
                        <h3 className="font-bold text-stone-900 mb-1">{isRTL ? 'الهاتف' : 'Phone'}</h3>
                        <a 
                          href={`tel:${menu.business.contactNumber}`} 
                          className="font-medium"
                          style={{ color }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = darker
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = color
                          }}
                        >
                          {menu.business.contactNumber}
                        </a>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-start gap-4">
                    <div 
                      className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: opacity15 }}
                    >
                      <Mail className="w-6 h-6" style={{ color }} />
                    </div>
                    <div>
                      <h3 className="font-bold text-stone-900 mb-1">{isRTL ? 'البريد الإلكتروني' : 'Email'}</h3>
                      <a 
                        href="mailto:info@example.com" 
                        className="font-medium"
                        style={{ color }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = darker
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = color
                        }}
                      >
                        info@example.com
                      </a>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card variant="elevated" className="border-stone-200">
              <CardContent className="p-6 sm:p-8">
                <h2 className="text-xl font-bold text-stone-900 mb-4">
                  {isRTL ? 'ساعات العمل' : 'Working Hours'}
                </h2>
                <div className="space-y-3 text-stone-600">
                  <div className="flex justify-between">
                    <span>{isRTL ? 'السبت - الخميس' : 'Saturday - Thursday'}</span>
                    <span className="font-medium">9:00 AM - 11:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{isRTL ? 'الجمعة' : 'Friday'}</span>
                    <span className="font-medium">2:00 PM - 11:00 PM</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </>
  )
}

